export const MARKETING = {
  product: "Videra PM",
  legalName: "Videra Technology Limited",
  salesEmail: "hello@videra.app",
  company: {
    name: "Videra Technology Limited",
    number: "17380198",
    addressLines: [
      "2nd Floor College House",
      "17 King Edwards Road",
      "Ruislip",
      "London HA4 7AE",
      "United Kingdom",
    ],
    email: "hello@videra.app",
    jurisdiction: "Registered in England and Wales",
    vatNumber: "",
  },
  tour: {
    overline: "Guided tour",
    heading: "Eight minutes inside a live portfolio",
    sub:
      "This is not a mock-up. Every screen below is a real workspace: ten capital schemes, three portfolios, 56 open risks, and a governance framework holding it all together. Follow it in order and you will have seen the whole product.",
    videoUrl: "",
    videoPoster: "/tour/dashboard.jpg",
    videoPending: {
      heading: "The narrated version lands shortly",
      body:
        "The full voiced walkthrough is in production. Until it publishes, scroll the seven chapters below \u2014 same story, same screens, and you can read it in half the time.",
    },
    workspace: {
      label: "The workspace you are looking at",
      items: [
        ["Meridian Delivery Group", "A construction and capital works organisation"],
        ["10 schemes", "Across 3 capital portfolios and 5 works programmes"],
        ["56 risks, 26 issues", "Live, owned and scored \u2014 not a spreadsheet"],
        ["60% assurance", "Framework compliance, measured not assumed"],
      ],
    },
    chaptersOverline: "The walkthrough",
    chaptersHeading: "Seven chapters. Every one a real screen.",
    chapters: [
      {
        icon: "LayoutDashboard",
        title: "The answer, before the question",
        minutes: "Chapter 01",
        image: "/tour/dashboard.jpg",
        lead: "Your sponsor asks how it is going. Most PMOs lose a morning finding out.",
        body:
          "This is the first screen after sign-in. Ten schemes, three portfolios, live status, open exposure and average progress \u2014 assembled from the records the delivery teams already keep. Nobody built this page. It is simply what the data says today.",
        callouts: [
          "60% framework compliance, scored automatically against the organisation\u2019s own gates",
          "56 open risks and 26 open issues, aggregated live across every scheme",
          "RAG by portfolio, so the weak spot is obvious before anyone asks",
        ],
        takeaway: "Reporting stops being an activity and becomes a property of the system.",
      },
      {
        icon: "Table2",
        title: "Every risk in the organisation, on one page",
        minutes: "Chapter 02",
        image: "/tour/risks.jpg",
        lead: "Risks usually hide in project-level spreadsheets, and surface only once they have cost something.",
        body:
          "This is the cross-portfolio risk register: 56 records from ten schemes, each with a reference, owner, probability and impact score, derived risk level, RAG and review date. Group it, filter it, save the view for your governance board, and export it to Excel when someone insists.",
        callouts: [
          "Scored 1\u20135 on probability and impact, with the risk level derived for you",
          "Group by portfolio, programme, type or owner \u2014 and save that view for the whole workspace",
          "Flag the three that matter and they carry straight into the weekly report",
        ],
        takeaway: "One register, every scheme. No consolidation, no version drift.",
      },
      {
        icon: "FileText",
        title: "The scheme record, in one place",
        minutes: "Chapter 03",
        image: "/tour/project.jpg",
        lead: "Ownership, governance, budget, dates and narrative usually live in four different documents.",
        body:
          "Harbour Road Junction Upgrade: reference HRJU-005, under the Capital Works Framework, 68% complete, gate approved. Owner, project manager, sponsor, business owner and PMO coordinator all named on the record, with the business case background and objectives alongside them.",
        callouts: [
          "A governance summary showing exactly what is logged against this scheme",
          "Stage, gate status and framework visible in the header, not buried in a document",
          "The whole record editable in place \u2014 no separate change form",
        ],
        takeaway: "When the scheme changes hands, the context goes with it.",
      },
      {
        icon: "ShieldAlert",
        title: "Nothing slips through the middle",
        minutes: "Chapter 04",
        image: "/tour/raid.jpg",
        lead: "Risks, issues, assumptions, decisions, variations, dependencies, lessons and benefits \u2014 all in one tab.",
        body:
          "Ground conditions differing from survey. A consent delayed. A key role vacant since the stage began. Each one owned, typed to this organisation\u2019s own list, scored and dated. When a risk becomes real, one action converts it to an issue and its history follows.",
        callouts: [
          "Risk types drawn from your sector, not ours \u2014 Ground Conditions, Planning & Consents, Site Access",
          "Risk to issue conversion keeps the audit thread intact",
          "Import a register from Excel; export it back whenever you need to",
        ],
        takeaway: "The register is where the work happens, so it is actually current.",
      },
      {
        icon: "GanttChartSquare",
        title: "A plan your planner will respect",
        minutes: "Chapter 05",
        image: "/tour/plan.jpg",
        lead: "This is not a task board with dates on it.",
        body:
          "21 tasks, 7 milestones, 2 on the critical path, dated from a framework template and baselined on application. Working calendars, all four dependency types with lag, total and free slack, constraints and variance \u2014 with 37 columns you can show, hide and reorder.",
        callouts: [
          "Import from Microsoft Project, Excel or CSV \u2014 and export straight back to it",
          "Critical path, baseline ghosts and milestone diamonds on the timeline",
          "Save a tailored plan as the template the next scheme starts from",
        ],
        takeaway: "Serious scheduling, without asking anyone to leave the platform.",
      },
      {
        icon: "ShieldCheck",
        title: "Assurance you can put a number on",
        minutes: "Chapter 06",
        image: "/tour/compliance.jpg",
        lead: "\u201cAre we following our own governance?\u201d is usually answered with an opinion.",
        body:
          "Here it is answered with 60%. Every scheme is scored against five assurance checks \u2014 gate packs uploaded, standard risks assessed, a current status report, key dates in place, and the stage gate approved. Three green, one amber, six red, and the weakest check named at the top.",
        callouts: [
          "Per-scheme matrix showing precisely which evidence is missing",
          "Portfolio and programme filters, so each director sees their own position",
          "Exported as an Excel or PDF assurance pack in one click",
        ],
        takeaway: "Governance becomes measurable, so it can actually improve.",
      },
      {
        icon: "Lock",
        title: "The conversation with IT",
        minutes: "Chapter 07",
        image: "/tour/assurance.jpg",
        lead: "This is the chapter that decides whether you are allowed to buy.",
        body:
          "A live assurance statement, generated from the workspace\u2019s actual configuration: which engine is in use, whether it is bound to your tenant, the redaction rules in force, retention, and how many calls have been logged. Export it as a PDF and forward it to information governance.",
        callouts: [
          "Point the assistant at your own Azure OpenAI, or lock it to internal-only",
          "Credentials, contacts, money and identifiers redacted before anything leaves",
          "Switch AI off entirely and every other feature carries on unchanged",
        ],
        takeaway: "Assurance we engineered, rather than assurance we wrote.",
      },
    ],
    replaces: {
      overline: "What it replaces",
      heading: "Four tools, one afternoon",
      items: [
        "The portfolio spreadsheet that three people maintain",
        "The plan that only opens on one laptop",
        "The risk log nobody reads between reporting cycles",
        "The monthly pack that is stale before it is presented",
      ],
    },
    cta: {
      heading: "The workspace you just toured is the one you get",
      body:
        "Start a trial, choose your sector, and a fully populated portfolio like this one is waiting within seconds \u2014 dashboards, registers, plans, gates and reports. Explore it, then clear it out and load your own.",
    },
  },
  tagline: "Know where you stand. Prove how you got there.",
  strapline: "Operational Excellence — powered by Videra Technology",
  sub:
    "Spreadsheets tell you what happened. Task boards tell you who's busy. Neither tells you whether the programme will land, or what you'll say when someone asks how you knew. Videra PM holds the plan, the risks, the decisions and the reporting as one record — so the position is current on Monday morning, and still defensible a year later.",
  problem: {
    heading: "Why good delivery teams outgrow the tools they have",
    pairs: [
      {
        pain: "The plan lives in Microsoft Project, on one laptop, and everyone else works from a screenshot of it",
        fix: "The schedule lives in the platform — critical path, baselines and dependencies intact, visible to everyone who needs it, and still exportable to Project whenever you want it back.",
      },
      {
        pain: "The task board is beautifully up to date, and still cannot tell you whether the programme will land",
        fix: "Videra reports position rather than activity: status, exposure, slippage and benefits, rolling up from project to programme to portfolio on their own.",
      },
      {
        pain: "Risks sit in a spreadsheet tab that nobody opens between reporting cycles",
        fix: "Live registers with owners, scores and review dates — and a platform that chases what's overdue so you don't have to.",
      },
      {
        pain: "Every week begins with the same consolidation exercise, and the pack is stale before it's read",
        fix: "The report is drafted from the records your team already keep. You correct it, approve it, and it's done before the meeting.",
      },
      {
        pain: "Six months later nobody can evidence who agreed what — and it lands on the person still in the room",
        fix: "Decisions, gates and approvals carry their owner, date, rationale and trail from the moment they happen.",
      },
      {
        pain: "The enterprise PPM suite promised control and delivered a nine-month implementation",
        fix: "Sign up in the morning, configured for your sector, populated and reporting by the afternoon. No mobilisation. No consultancy line item.",
      },
    ],
  },
  alternatives: {
    overline: "Straight talk",
    heading: "Where the other options stop",
    body:
      "Most organisations aren't starting from nothing. They're starting from four tools that each do a quarter of the job, and a monthly ritual that stitches them together. Here is where each one runs out of road.",
    items: [
      {
        icon: "Table2",
        name: "Spreadsheets and SharePoint",
        stops:
          "Infinitely flexible, endlessly copied. The truth splits the moment two people open it at once, and version control becomes somebody's job.",
      },
      {
        icon: "KanbanSquare",
        name: "Task trackers",
        stops:
          "Excellent at work in motion. But without gates, scored risk or an approval trail, there's nothing to show when the programme is challenged.",
      },
      {
        icon: "GanttChartSquare",
        name: "Microsoft Project",
        stops:
          "Serious scheduling, built for one planner. Governance, reporting and assurance all happen somewhere else, by hand, every week.",
      },
      {
        icon: "Building2",
        name: "Enterprise PPM suites",
        stops:
          "Real capability, sold with a long implementation, a consultancy retainer and a licence that's hard to justify below a few hundred seats.",
      },
    ],
    close:
      "Videra PM sits where most delivery organisations actually live: governance-grade discipline, running this week, at a price a PMO can sign off without a business case.",
  },
  whatItDoes: {
    overline: "How it's built",
    heading: "One system. One record. One version of the truth.",
    body:
      "Most platforms in this market began life as separate modules and later learned to talk to each other. Videra PM was built as a single vertical system. Move a milestone in the schedule and the project's status, the programme rollup and the portfolio dashboard change with it — not because an integration fired overnight, but because there was only ever one record underneath.",
  },
  roles: {
    overline: "Who it answers to",
    heading: "Four people. Four questions. One record.",
    items: [
      ["The executive sponsor", "\u201cAre we going to land this?\u201d Answered on one page, live, without anyone preparing a pack."],
      ["The programme director", "\u201cWhat's about to hurt me?\u201d Dependencies, exposure and slippage surfaced while there's still time."],
      ["The PMO lead", "\u201cIs everyone working the same way?\u201d Yes — because the method is built into the tool, not into a policy document."],
      ["IT and information governance", "\u201cWhere does our data go?\u201d Nowhere you haven't approved. Evidence provided, not promised."],
    ],
  },
  sectors: [
    {
      icon: "Landmark",
      title: "Place-based and infrastructure programmes",
      body:
        "Eleven projects, four delivery partners, and a strategic outcome you'll be asked to prove. Pull them into one programme, follow the regeneration and investment benefits from business case to realised, and brief members from the same live record you manage from.",
      span: "lg:col-span-7",
    },
    {
      icon: "ClipboardCheck",
      title: "Operational readiness",
      body:
        "The date is fixed and readiness currently lives in one person's spreadsheet. Gate-controlled stages, evidence attached where it belongs, and a readiness position you can take to a board instead of rebuilding it the night before.",
      span: "lg:col-span-5",
    },
    {
      icon: "HardHat",
      title: "Construction",
      body:
        "You inherit a scheme mid-flight with no gate record and a client who wants assurance. Stages, variations, decisions and handback readiness in one place, in your language, and a defensible position by the end of the week.",
      span: "lg:col-span-5",
    },
    {
      icon: "HeartPulse",
      title: "Healthcare",
      body:
        "Capital schemes, compliance evidence, and an executive team that wants one page. Portfolio visibility without the monthly consolidation, and an assurance position your information governance team will actually sign.",
      span: "lg:col-span-7",
    },
    {
      icon: "Server",
      title: "Technology and transformation",
      body:
        "Change arrives faster than anyone can assess it. Gated stages, technical registers and change control that keep pace — with every dependency on the neighbouring workstream visible before it bites.",
      span: "lg:col-span-7",
    },
    {
      icon: "Briefcase",
      title: "Professional services and delivery partners",
      body:
        "The client expects your delivery infrastructure on day one of the engagement. Give each of them a branded workspace, governed to the same standard, without rebuilding it from scratch every time you win.",
      span: "lg:col-span-5",
    },
  ],
  consistency: {
    overline: "Consistency",
    heading: "Set the standard once. Every programme after it inherits it.",
    body:
      "Governance only works when it's the path of least resistance. Configure your gates, stages and evidence once, and every project, client and department that follows arrives already compliant — which is why a new project manager can be useful in their first week instead of their first quarter.",
    points: [
      "Your framework applied to a new project — stages, gates and required evidence — in one click",
      "The plan that worked becomes the template the next team starts from",
      "Fourteen sector configurations, so the platform speaks your words, not ours",
      "Reporting periods that open, close and chase themselves",
      "Rollup from project to programme to portfolio as a property of the data, not a monthly ritual",
      "Status reports, progress packs and board exports generated on demand",
    ],
    footnote:
      "Live the same afternoon. No implementation programme, no consultancy, no six-month mobilisation.",
  },
  defensibility: {
    overline: "The difference",
    heading: "The dashboard is the easy part",
    body:
      "Any tool can draw a chart. Very few can answer the question that actually matters when a programme is challenged: what did we know, when did we know it, who decided, and what did we do about it? That answer is either in your system or it isn't — and you find out which at the worst possible moment.",
    points: [
      ["Decisions on the record", "The owner, the date and the reasoning, captured as the decision is taken rather than remembered afterwards."],
      ["Approval trails", "Every gate and report carries who asked, who approved, what they said and when."],
      ["Dependencies with two signatures", "The receiving project accepts or rejects in the system, so \u201cwe were never asked\u201d stops being an option."],
      ["Benefits followed to realised", "The outcomes you promised in the business case, tracked with evidence rather than optimism."],
      ["Compliance scored", "Where governance is complete, where it's thin, and precisely which record is missing."],
      ["A full audit trail", "Including every assisted call, its redacted prompt and its response."],
    ],
    close:
      "Good delivery should survive the person who ran it. That is what a record is for — and why the people who own one get promoted rather than kept.",
  },
  cta: {
    heading: "Running this week. Not next quarter.",
    body:
      "Choose a plan, and your configured workspace is ready immediately. No setup phase, no professional services, no waiting for someone else's project team to become available.",
  },
  proofPoints: [
    { value: "14", label: "Sector configurations" },
    { value: "7 days", label: "Free trial, no card" },
    { value: "100%", label: "Still works with AI switched off" },
  ],
  assurances: [
    "UK and EU data residency",
    "AI inside your own tenant, or no AI at all",
    "Full decision and approval audit trail",
    "Role-based access and workspace isolation",
  ],
  security: {
    overline: "Data boundary",
    title: "Your data. Your tenant. Your evidence.",
    body:
      "Most platforms hand you an AI feature and leave you to explain it to your information governance team. We took the opposite view. Point the assistant at your own Azure OpenAI resource and redacted payloads never leave your tenant, your region or your own data processing agreement. Assurance is something we built, not something we wrote.",
    points: [
      "Your own Azure OpenAI endpoint — processing stays inside your tenant",
      "An internal-only lock that refuses external calls rather than quietly falling back",
      "Credentials, contacts, money and identifiers redacted before anything leaves",
      "Named colleagues pseudonymised, so the output still reads properly",
      "Every call logged with its redacted prompt, its response and a verification digest",
      "An assurance statement generated from the live configuration, as a PDF",
    ],
    footnote:
      "And if the answer is simply no, switch the assistant off. Nothing else in the platform depends on it.",
  },
  faqs: [
    {
      q: "How is this different from monday.com, Asana or Microsoft Planner?",
      a:
        "Those are excellent at coordinating work. They were never built to govern it. There is no scored risk register, no stage gate, no routed approval and no audit trail — so when a programme is questioned, there's nothing to show. Videra PM starts where they stop.",
    },
    {
      q: "We already use Microsoft Project. Do we have to give it up?",
      a:
        "No. Import your plans, work on them here where the rest of the organisation can see them, and export back to Project whenever you like. Scheduling is native — critical path, slack, constraints, baselines and variance — so nothing is lost in translation.",
    },
    {
      q: "How long does implementation take?",
      a:
        "There isn't one. Choose your sector at sign-up and your gates, risk types, frameworks and terminology are configured for you, with an optional worked portfolio to explore. Most workspaces are reporting on real projects within a day.",
    },
    {
      q: "Does it still work with AI switched off?",
      a:
        "Completely. The assistant supports six areas — report drafting, status drafts, classification, mitigation suggestions, triage and chase-ups — and each falls back to typing it yourself. Every register, schedule, approval, report and dashboard is untouched.",
    },
    {
      q: "Where is our data processed?",
      a:
        "Your workspace data stays in the platform database, hosted in the UK and EU. If you enable the assistant you choose the engine: your own Azure OpenAI resource, keeping processing inside your tenant and region, or a shared gateway. The assurance statement documents whichever you pick.",
    },
    {
      q: "Can we make it look and sound like us?",
      a:
        "Yes. Professional and Enterprise include full white-labelling of logo, colours and product name, plus sector configurations that change the language throughout — so a construction client sees schemes and variations where an NHS client sees projects and change controls.",
    },
    {
      q: "What happens when the trial ends?",
      a:
        "The workspace goes read-only. Nothing is deleted, nothing is lost, and everything you built is waiting the moment you choose a plan.",
    },
    {
      q: "Are you on public sector frameworks?",
      a:
        "We're working towards a G-Cloud Digital Marketplace listing so buyers can procure through an existing framework. In the meantime, talk to us about your route to purchase — we've done it the direct way plenty of times.",
    },
  ],
};
